"use client";

import { useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { Check, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { locales, localeNames, type Locale } from "../../../i18n/config";
import { postBySlug } from "@/lib/blog-posts";

export function LocaleSwitcher() {
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();

  function switchLocale(newLocale: Locale) {
    if (newLocale === locale) return;
    const segments = pathname.split("/");
    segments[1] = newLocale;

    // Les articles ne sont pas tous traduits (`locales` dans blog-posts.ts) :
    // basculer sur une langue non rédigée menait droit à une 404. On retombe
    // alors sur l'index du blog, qui existe dans les trois langues.
    if (segments[2] === "blog" && segments[3]) {
      const post = postBySlug(segments[3]);
      if (post && !post.locales.includes(newLocale)) {
        router.replace(`/${newLocale}/blog`);
        return;
      }
    }

    // Querystring conservée : sans elle, `?tab=...` (réglages) et les UTM
    // étaient perdus au changement de langue. Lue sur `window` plutôt qu'avec
    // `useSearchParams`, dont le hook imposerait une frontière Suspense à tous
    // les en-têtes statiques — ce code ne tourne qu'au clic.
    const query = window.location.search;
    router.replace(segments.join("/") + query);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            aria-label={localeNames[locale]}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          />
        }
      >
        <Globe className="h-4 w-4" />
        {locale.toUpperCase()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-40">
        {locales.map((l) => (
          <DropdownMenuItem
            key={l}
            onClick={() => switchLocale(l)}
            disabled={l === locale}
            className="justify-between"
          >
            {localeNames[l]}
            {l === locale ? <Check className="h-4 w-4" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
